import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {
  AntibodySchemaDomainLinkedSchemasQuery,
  AntibodySchemaDomainLinkedSchemasQueryVariables,
} from 'antibodies/actions/__generated/AntibodySchemaDomainLinkedSchemasQuery';
import {ANTIBODY_SCHEMA_DOMAIN_LINKED_SCHEMAS_QUERY} from 'antibodies/actions/AntibodyActionUtil';
import {AntibodyDomainSearchInput} from 'antibodies/actions/AntibodyDomainSearchInput';
import {ANTIBODY_REST_PROPS_BY_ID} from 'antibodies/AntibodyConstants';
import {AntibodyFormat, DistinctDomain} from 'antibodies/AntibodyTypedefs';
import {distinctDomainToKey, formatToSortedDistinctDomains} from 'antibodies/AntibodyUtils';
import Antibody from 'antibodies/visualization/Antibody';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {FONT_PROXIMA_12} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

export type DomainWithProtein = DistinctDomain & {proteinId: string | null};

export interface AntibodyDomainInputsProps {
  antibodySchemaId: string;
  disabled: boolean;
  format: AntibodyFormat;
  domainsWithProteinByKey: Record<string, DomainWithProtein>;
  setDomainsWithProteinByKey: (newDomainsWithProteinByKey: Record<string, DomainWithProtein>) => void;
}

/**
 * A set of ChipSearchInput that can be used to associate AA Sequence entities with antibody domains.
 * @param schemaId The API ID of the antibody schema
 * @param disabled Whether the ChipSearchInput should be disabled
 * @param format The format of the antibody being created
 * @param domainsWithProteinByKey A map domain keys to domains with their associated protein
 * @param setDomainsWithProteinByKey A function to update domainsWithProteinByKey
 */
export default function AntibodyDomainInputs({
  antibodySchemaId,
  disabled,
  format,
  domainsWithProteinByKey,
  setDomainsWithProteinByKey,
}: AntibodyDomainInputsProps): JSX.Element {
  const antibodyRestProps = ANTIBODY_REST_PROPS_BY_ID[format.id];
  const sortedDistinctDomains = formatToSortedDistinctDomains(format);

  const domainLinkedSchemasQueryResult = useBenchlingQuery<
    AntibodySchemaDomainLinkedSchemasQuery,
    AntibodySchemaDomainLinkedSchemasQueryVariables
  >(ANTIBODY_SCHEMA_DOMAIN_LINKED_SCHEMAS_QUERY, {variables: {schemaId: antibodySchemaId}});

  const linkedSchemasByDomain = useMemo(() => {
    if (
      domainLinkedSchemasQueryResult.type !== QueryResultType.SUCCESS ||
      domainLinkedSchemasQueryResult.data.implementedAntibodySchemaInterface == null ||
      isInaccessible(domainLinkedSchemasQueryResult.data.implementedAntibodySchemaInterface)
    ) {
      return {};
    }
    const {domainLinkedSchemas} = domainLinkedSchemasQueryResult.data.implementedAntibodySchemaInterface;
    return domainLinkedSchemas.reduce(
      (acc, spec) => {
        acc[distinctDomainToKey({type: spec.domainType, specificity: spec.specificity})] =
          spec.schemaOrInterfaceId;
        return acc;
      },
      {} as Record<string, string | null>
    );
  }, [domainLinkedSchemasQueryResult]);

  if (domainLinkedSchemasQueryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }
  const domainSchemasQueryError =
    domainLinkedSchemasQueryResult.type === QueryResultType.ERROR ||
    domainLinkedSchemasQueryResult.data.implementedAntibodySchemaInterface == null ||
    isInaccessible(domainLinkedSchemasQueryResult.data.implementedAntibodySchemaInterface);

  return (
    <div className={css(styles.wrapper)}>
      <div className={css(styles.column)}>
        <div className={css(styles.columnHeader)}>Preview</div>
        <div className={css(styles.vizWrapper)}>
          <Antibody format={format} {...antibodyRestProps} />
        </div>
      </div>
      <div className={css(styles.column)}>
        {domainSchemasQueryError && (
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text="Failed to load antibody domain configuration"
          />
        )}
        <div className={css(styles.columnHeader)}>Domains</div>
        <div>
          {sortedDistinctDomains.map((distinctDomain) => {
            const distinctDomainKey = distinctDomainToKey(distinctDomain);
            return (
              <AntibodyDomainSearchInput
                key={distinctDomainKey}
                disabled={disabled || domainSchemasQueryError}
                distinctDomain={distinctDomain}
                selectedProtein={domainsWithProteinByKey[distinctDomainKey]?.proteinId}
                setSelectedProtein={(newSelectedProtein) => {
                  setDomainsWithProteinByKey({
                    ...domainsWithProteinByKey,
                    [distinctDomainKey]: {
                      ...distinctDomain,
                      proteinId: newSelectedProtein,
                    },
                  });
                }}
                linkedSchemaId={linkedSchemasByDomain[distinctDomainKey]}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  column: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  columnHeader: {
    ...FONT_PROXIMA_12,
    color: Color.GRAY_700,
    fontWeight: 600,
    marginBottom: '8px',
  },
  vizWrapper: {
    backgroundColor: Color.GRAY_50,
    borderRadius: '8px',
    padding: '32px',
    maxHeight: '400px',
  },
  wrapper: {
    display: 'flex',
    gap: '16px',
  },
});
